import { getSectorColor } from '@/lib/sector-colors';
import { dotRadius, type PlotStock } from '@/lib/scatter2d-helpers';

interface Props {
  sortedStocks: PlotStock[];
  highlightSymbol: string;
  pinnedSymbols: Set<string>;
  hoveredSymbol: string | null;
  enteredAlphaSymbols?: Set<string>;
  exitedAlphaSymbols?: Set<string>;
}

export function StockDotsLayer({
  sortedStocks,
  highlightSymbol,
  pinnedSymbols,
  hoveredSymbol,
  enteredAlphaSymbols,
  exitedAlphaSymbols,
}: Props) {
  return (
    <>
      {sortedStocks.map(s => {
        const isHighlighted = !!(highlightSymbol && s.symbol === highlightSymbol);
        const isHovered = hoveredSymbol === s.symbol;
        const isPinned = pinnedSymbols.has(s.symbol);
        const color = getSectorColor(s.sector);
        const r = dotRadius(s.marketCap, isHighlighted, isHovered, isPinned);
        const isEnterAlpha = enteredAlphaSymbols?.has(s.symbol) ?? false;
        const isExitAlpha = exitedAlphaSymbols?.has(s.symbol) ?? false;
        const hasSignal = isEnterAlpha || isExitAlpha;
        const signalColor = isEnterAlpha ? '#22c55e' : isExitAlpha ? '#dc2626' : null;
        return (
          <g key={`dot-${s.symbol}`}>
            {isEnterAlpha && !isHighlighted && !isPinned && (
              <>
                <circle cx={s.px} cy={s.py} r={r + 6} fill="none" stroke="#22c55e" strokeWidth={2} opacity={0.85}>
                  <animate attributeName="r" values={`${r + 3};${r + 9};${r + 3}`} dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.9;0.3;0.9" dur="2s" repeatCount="indefinite" />
                </circle>
                <circle cx={s.px} cy={s.py} r={r + 3} fill="none" stroke="#22c55e" strokeWidth={1} opacity={0.5} />
              </>
            )}
            {isExitAlpha && !isHighlighted && !isPinned && (
              <>
                <circle cx={s.px} cy={s.py} r={r + 5} fill="none" stroke="#dc2626" strokeWidth={2} opacity={0.85}>
                  <animate attributeName="opacity" values="1;0.25;1" dur="1.2s" repeatCount="indefinite" />
                </circle>
                <circle cx={s.px} cy={s.py} r={r + 9} fill="none" stroke="#dc2626" strokeWidth={1} opacity={0.4}>
                  <animate attributeName="r" values={`${r + 6};${r + 12};${r + 6}`} dur="1.5s" repeatCount="indefinite" />
                </circle>
              </>
            )}
            <circle
              cx={s.px} cy={s.py} r={hasSignal && !isHighlighted && !isPinned ? r + 1 : r}
              fill={color}
              opacity={
                isHighlighted || isPinned ? 1
                : highlightSymbol ? (isHovered ? 0.35 : 0.12)
                : isHovered ? 0.95 : 0.82
              }
              stroke={isHighlighted ? 'white' : isPinned ? 'white' : isHovered ? 'rgba(255,255,255,0.7)' : signalColor ?? 'rgba(0,0,0,0.4)'}
              strokeWidth={isHighlighted ? 2.5 : isPinned ? 2 : isHovered ? 1.5 : hasSignal ? 2 : 0.5}
              strokeDasharray={isPinned && !isHighlighted ? '3 2' : undefined}
              filter={isHighlighted || isPinned ? 'url(#glow)' : undefined}
              style={{ transition: 'r 150ms ease, stroke-width 150ms ease, opacity 200ms ease' }}
            />
          </g>
        );
      })}
    </>
  );
}
